/**
 * TaxonomyModal Component
 *
 * Modal for adding/editing taxonomy items (property types, amenities, etc.).
 */

import { useState, useEffect } from 'react';
import type { TaxonomyItem } from '../hooks';

interface TaxonomyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<TaxonomyItem>) => Promise<void>;
  initialData?: TaxonomyItem | null;
  type: 'propertyType' | 'amenity' | 'roomType';
  categories?: string[];
}

const COMMON_ICONS = [
  '🏠', '🏢', '🏡', '🏔️', '🛋️', '🌆', '🌾', '🏖️',
  '📶', '❄️', '🔥', '🚿', '🛁', '🏊', '🛀', '🌳',
  '🌅', '🌇', '🍳', '🧊', '📻', '🍽️', '☕', '📺',
  '🎬', '🎮', '🅿️', '🚗', '🔔', '🧯', '🩹', '🛏️',
  '🚿', '🧹', '🧺', '👶', '🐕', '🚭', '🎉', '💼',
];

export const TaxonomyModal = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  type,
  categories = [],
}: TaxonomyModalProps) => {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [isPopular, setIsPopular] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);

  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setIcon(initialData.icon || '');
      setCategory(initialData.category || '');
      setDescription(initialData.description || '');
      setIsPopular(initialData.isPopular || false);
    } else {
      setName('');
      setIcon('');
      setCategory('');
      setDescription('');
      setIsPopular(false);
    }
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!name.trim()) return;

    setIsSubmitting(true);
    try {
      await onSave({
        name: name.trim(),
        icon: icon || undefined,
        category: category || undefined,
        description: description || undefined,
        isPopular,
      });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTitle = () => {
    const action = initialData ? 'Edit' : 'Add';
    switch (type) {
      case 'propertyType':
        return `${action} Property Type`;
      case 'amenity':
        return `${action} Amenity`;
      case 'roomType':
        return `${action} Room Type`;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4">{getTitle()}</h2>

          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter name..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Icon */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Icon
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowIconPicker(!showIconPicker)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 min-w-[80px]"
                >
                  {icon ? (
                    <span className="text-2xl">{icon}</span>
                  ) : (
                    <span className="text-gray-400">Select</span>
                  )}
                </button>
                {icon && (
                  <button
                    type="button"
                    onClick={() => setIcon('')}
                    className="px-3 py-2 text-gray-500 hover:text-red-500"
                  >
                    Clear
                  </button>
                )}
              </div>
              {showIconPicker && (
                <div className="mt-2 p-3 border border-gray-200 rounded-lg bg-gray-50 grid grid-cols-8 gap-2">
                  {COMMON_ICONS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => {
                        setIcon(emoji);
                        setShowIconPicker(false);
                      }}
                      className={`p-2 text-xl rounded hover:bg-white ${
                        icon === emoji ? 'bg-primary-100 ring-2 ring-primary-500' : ''
                      }`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Category (amenities only) */}
            {type === 'amenity' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select category...</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                  <option value="__new__">+ Add new category</option>
                </select>
                {category === '__new__' && (
                  <input
                    type="text"
                    placeholder="Enter new category name..."
                    onChange={(e) => setCategory(e.target.value)}
                    className="mt-2 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                )}
              </div>
            )}

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description..."
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Popular flag (amenities only) */}
            {type === 'amenity' && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPopular}
                  onChange={(e) => setIsPopular(e.target.checked)}
                  className="w-4 h-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">
                  Mark as popular amenity
                </span>
              </label>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!name.trim() || isSubmitting}
              className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : initialData ? 'Update' : 'Add'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxonomyModal;
